import type { CountryCode } from '@module/apis/client-side/live/type';
import { globalChatVisibleSafeLocalStorage } from '@module/lib/storage/storageImplementations';
import type { FirstSponMessage, IntroEffectMessage } from '@module/types/centrifuge/public-message/etcMessageTypes';
import type { EventAnimCoinMessage } from '@module/types/centrifuge/public-message/eventMessageTypes';
import { create } from 'zustand';
import type { ChatUIAffectingMessage } from '../types/centrifuge/chatTypes';
import type { ItemCoinMessage, SponCoinMessage } from '../types/centrifuge/public-message/sponCoinMessageTypes';

export type SponItem = SponCoinMessage | ItemCoinMessage;

interface ChatMessageStoreState {
  chatMessages: ChatUIAffectingMessage[];
  setChatMessages: (
    chatItems: ChatUIAffectingMessage[] | ((prev: ChatUIAffectingMessage[]) => ChatUIAffectingMessage[]),
  ) => void;
  sponMessages: SponItem[];
  setSponMessages: (sponItems: SponItem[] | ((prev: SponItem[]) => SponItem[])) => void;
  supporterNotice: string;
  setSupporterNotice: (notice: string) => void;

  /**
   * 브릿지 트리거 시스템
   *
   * 컴포넌트 간 직접적인 의존성 없이 이벤트를 전달하기 위한 트리거 함수들
   * 사용법:
   * 1. 트리거를 받을 컴포넌트에서 함수를 등록
   * 2. 트리거를 발생시킬 곳에서 해당 함수 호출
   *
   * @example
   * // 트리거 등록 (수신측 컴포넌트)
   * useEffect(() => {
   *   useChatMessageStore.getState().triggers.introEffect = () => {
   *     // implementation
   *   };
   * }, []);
   *
   * // 트리거 발생 (송신측)
   * const { introEffect } = useChatMessageStore.getState().triggers;
   * introEffect?.(messageData);
   */
  triggers: {
    introEffect: ((msg: IntroEffectMessage['data']['message']) => void) | null; // 모바일 only
    castSettingRecommendEffect: (() => void) | null; // 방송 설정 only
    playCastSettingKingFanInAudio: (() => void) | null; // 방송 설정 only
    playCastSettingFanInAudio: (() => void) | null; // 방송 설정 only
    eventAnimCoin: ((payload: EventAnimCoinMessage) => void) | null;
    firstSpon: ((payload: FirstSponMessage) => void) | null; // fanza only
    sponThanks: (() => void) | null; // fanza-mobile only
  };

  isGlobalChatVisible: boolean; // fanza only
  setIsGlobalChatVisible: (value: boolean) => void; // fanza only
  preBlockedLocales: CountryCode[];
  setPreBlockedLocales: (locales: CountryCode[]) => void;
}

const useChatMessageStore = create<ChatMessageStoreState>((set) => ({
  chatMessages: [],
  setChatMessages: (chatItems) =>
    set((state) => ({
      chatMessages: typeof chatItems === 'function' ? chatItems(state.chatMessages) : chatItems,
    })),
  sponMessages: [],
  setSponMessages: (sponItems) =>
    set((state) => ({
      sponMessages: typeof sponItems === 'function' ? sponItems(state.sponMessages) : sponItems,
    })),
  supporterNotice: '',
  setSupporterNotice: (notice) => set({ supporterNotice: notice }),
  triggers: {
    introEffect: null,
    castSettingRecommendEffect: null,
    playCastSettingKingFanInAudio: null,
    playCastSettingFanInAudio: null,
    eventAnimCoin: null,
    firstSpon: null,
    sponThanks: null,
  },
  isGlobalChatVisible: globalChatVisibleSafeLocalStorage.get()
    ? globalChatVisibleSafeLocalStorage.get() === 'true'
    : true,
  setIsGlobalChatVisible: (value) => {
    set({ isGlobalChatVisible: value });
    globalChatVisibleSafeLocalStorage.set(value ? 'true' : 'false');
  },
  preBlockedLocales: [], // 이전 차단 목록, 서버에서 오는 값 X (변경사항 비교용)
  setPreBlockedLocales: (locales) => {
    set({ preBlockedLocales: locales });
  },
}));

export default useChatMessageStore;
